import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { Field, Form } from 'react-final-form';
import RadioGroupField from 'components/RadioGroupField';
import useCallbackRef from 'hooks/useCallbackRef';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Modal } from 'ncoded-component-library';
import TextAreaField from 'components/TextAreaField';
import {
  BM_PAYMENT_RESPONSE_STATUS,
  DashboardMetric,
  PaymentInvoiceSkippedFormProps,
} from '../../types';
import { getUserFullName, openMail } from 'utils';
import useHeightAnimation from 'hooks/useHeightAnimation';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { generateDaysLeftString } from '../../data';

import './PaymentInvoiceSkipped.styles.scss';

const INITIAL_STATE = {
  bmPaymentResponse: BM_PAYMENT_RESPONSE_STATUS.ACCEPTED,
};

type PaymentInvoiceSkippedProps = {
  className?: string;
  dashboardMetric: DashboardMetric;
  updateDashboardMetric: (DashboardMetric: Partial<DashboardMetric>) => void;
} & ModalProps;

const PaymentInvoiceSkipped: React.ForwardRefRenderFunction<
  ModalRef,
  PaymentInvoiceSkippedProps
> = (props, ref) => {
  const { className, dashboardMetric, updateDashboardMetric, ...rest } = props;

  const [modal, modalRef] = useCallbackRef<ModalRef>();
  const [container, containerRef] = useCallbackRef<HTMLDivElement>();

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const {
    isUltraAdmin,
    currentUser: { _id: currentUserId },
  } = useContext(CurrentUserContext);

  useHeightAnimation({
    element: container,
    defaultOpen: true,
  });

  useImperativeHandle(ref, () => modal, [modal]);

  const { t } = useTranslation();

  const classes = classNames('payment-invoice-skipped', className);

  const { _id, bmPaymentResponse, reasonBmPaymentResponse, listing, user } =
    dashboardMetric || {};

  const {
    paymentDueDate,
    paymentDueDateDescription,
    reasonForNotGeneratingInvoice,
    clientCompanyForGeneratingInvoice,
    clientEmailForGeneratingInvoice,
    clientFirstNameForGeneratingInvoice,
    clientLastNameForGeneratingInvoice,
  } = listing || {};

  const emailHref = openMail(clientEmailForGeneratingInvoice);

  const showForm = useMemo(
    () =>
      modal?.openState &&
      isUltraAdmin &&
      bmPaymentResponse === BM_PAYMENT_RESPONSE_STATUS.PENDING,
    [bmPaymentResponse, isUltraAdmin, modal?.openState],
  );

  const showRejectedMessage = useMemo(
    () =>
      reasonBmPaymentResponse &&
      bmPaymentResponse === BM_PAYMENT_RESPONSE_STATUS.REJECTED,
    [reasonBmPaymentResponse, bmPaymentResponse],
  );

  const actionOptions = useMemo(
    () => [
      { label: t('accept'), value: BM_PAYMENT_RESPONSE_STATUS.ACCEPTED },
      { label: t('reject'), value: BM_PAYMENT_RESPONSE_STATUS.REJECTED },
    ],
    [t],
  );

  const modalTitle = useMemo(() => {
    if (bmPaymentResponse === BM_PAYMENT_RESPONSE_STATUS.ACCEPTED)
      return 'acceptedInvoice';

    if (bmPaymentResponse === BM_PAYMENT_RESPONSE_STATUS.REJECTED)
      return 'rejectedInvoice';

    return 'invoiceInformation';
  }, [bmPaymentResponse]);

  const modalDescription = useMemo(
    () => (
      <Trans
        i18nKey={
          clientEmailForGeneratingInvoice
            ? 'bmInvoiceCreatedWithEmail'
            : 'bmInvoiceCreated'
        }
        values={{
          bmName: !user
            ? t('na')
            : user?._id === currentUserId
            ? t('you')
            : getUserFullName(user.firstName, user.lastName),
          companyName: clientCompanyForGeneratingInvoice || t('na'),
          clientEmail: clientEmailForGeneratingInvoice || t('na'),
          clientName: clientFirstNameForGeneratingInvoice
            ? getUserFullName(
                clientFirstNameForGeneratingInvoice,
                clientLastNameForGeneratingInvoice,
              )
            : t('na'),
        }}
        components={{
          email: (
            <a href={emailHref}>{clientEmailForGeneratingInvoice || t('na')}</a>
          ),
        }}
      />
    ),
    [
      emailHref,
      user,
      currentUserId,
      clientCompanyForGeneratingInvoice,
      clientEmailForGeneratingInvoice,
      clientFirstNameForGeneratingInvoice,
      clientLastNameForGeneratingInvoice,
      t,
    ],
  );

  const onSubmit = useCallback(
    async (values: PaymentInvoiceSkippedFormProps) => {
      try {
        const { data } = await api.portfolio.admin.paymentInvoiceRespond(
          _id,
          values,
        );

        updateDashboardMetric(data);
        popSuccess(
          t(
            values.bmPaymentResponse === BM_PAYMENT_RESPONSE_STATUS.REJECTED
              ? 'successFullyRejectedInvoice'
              : 'successFullyAcceptedInvoice',
          ),
        );

        modal?.close();
      } catch (e) {
        popServerError(e);
      }
    },
    [_id, modal, popServerError, popSuccess, t, updateDashboardMetric],
  );

  return (
    <Modal ref={modalRef} className={classes} title={t(modalTitle)} {...rest}>
      <div>
        <p>{modalDescription}</p>
        <ul>
          <li>
            {t('dueDateBy', {
              dueDate: generateDaysLeftString(paymentDueDate),
            })}
          </li>

          {paymentDueDateDescription && (
            <li>
              {t('dueDateDescriptiion', {
                paymentDueDateDescription,
              })}
            </li>
          )}

          <li>{t('reasonFor', { reason: reasonForNotGeneratingInvoice })}</li>
        </ul>

        {showRejectedMessage && (
          <p>{t('invoiceRejectedWithReason', { reasonBmPaymentResponse })}</p>
        )}
      </div>

      {showForm ? (
        <Form initialValues={INITIAL_STATE} onSubmit={onSubmit}>
          {({ values: { bmPaymentResponse }, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <p>{t('bmPendingInvoiceDescription')}</p>

              <RadioGroupField
                name="bmPaymentResponse"
                label={t('doYouAcceptOrRejectListing')}
                options={actionOptions}
              />

              <div ref={containerRef}>
                {bmPaymentResponse === BM_PAYMENT_RESPONSE_STATUS.REJECTED && (
                  <Field
                    name="reasonBmPaymentResponse"
                    label={t('reasononOfRejection')}
                    component={TextAreaField}
                    maxLength={1000}
                    rows={4}
                  />
                )}
              </div>

              <div className="actions">
                <Button variant="link" onClick={modal?.close}>
                  {t('cancel')}
                </Button>
                <Button type="submit">{t('confirm')}</Button>
              </div>
            </form>
          )}
        </Form>
      ) : (
        <div className="actions">
          <Button variant="link" onClick={modal?.close}>
            {t('close')}
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default forwardRef(PaymentInvoiceSkipped);
